// ---------------------------------------------------------------------------
// The room's own clock: when it closes, and the one way to push that back.
//
// A room with people in it and nothing happening is reclaimed after a while, and
// until this existed the first anybody knew about it was the room disappearing.
// Everything here is fed by `state.roomExpiresAt`, which is an ABSOLUTE epoch
// timestamp from the server and read against `serverNow()` — the same rule as
// every other clock in the app, for the same reason. Nothing here counts down
// locally from a number the server sent.
//
// This file holds the last-stretch strip; `RoomInfo.tsx` holds the header button
// and the panel behind it; `roomExpiryClock.ts` holds the deadline and the three
// ways of saying how long is left, which all of them import rather than
// reimplement. The whole design is written down HERE, popover included, because
// the popover is where the design ended up and splitting the reasoning across
// three headers is how the three drift.
//
// WHAT IS SHOWN, AND WHEN. A countdown that runs for two hours is noise, and
// worse than noise: it is a deadline nobody can do anything useful about, on
// screen for the entire time it does not matter.
//
//   over 10 minutes   the header button says `room` and nothing else. The
//                     reading, the explanation and the extend are one press
//                     away, inside the popover, and the popover is reachable at
//                     every level — including this one.
//   under 10 minutes  the button turns accent and shows the reading, to the
//                     minute.
//   under 2 minutes   the button turns alert and counts seconds, and a
//                     full-width strip appears under the header with the
//                     seconds and a button on it.
//
// **Amended once: the calm state used to be a bare icon with no number at all**,
// on the grounds that a two-hour countdown is noise and a deadline nobody needs
// to act on should not be on screen. That was right about the countdown and
// wrong about the button. Isaac's report was "I can see a timer icon in the
// header and I don't know what it does" — an unlabelled stopwatch that silently
// does something when pressed, with the only explanation in an `aria-label` a
// phone never shows.
//
// **Amended again, and the second amendment is what put the number back away.**
// The first fix was a number beside the stopwatch, on the reasoning that a
// number is what makes the control legible. It made the control READABLE and
// left it unexplained: Isaac's second report was that the two-hour button "does
// not make much sense to the end user who sees it". A pill reading `1h58` says
// what it is measuring and still does not say what happens at zero, what the
// press does, or why a room has a lifetime at all — and none of that fits in a
// header on a 360px phone.
//
// So the explanation is a sentence rather than a number, and it lives in the
// popover with the room code, the room's visibility and what the room is set to
// play. The header button is a WORD, `room`, which is legible in the way the
// stopwatch was not, and pressing it opens something to read instead of
// silently mutating the room. Both failure modes above are answered: it is
// labelled, and the press is no longer a hidden side effect.
//
// **The escalation still has to be visible from outside the popover**, because a
// host who never opens it must still see the room is about to close. That is why
// `soon` and `urgent` put the reading back on the button and tint it — the
// number returns exactly when it stops being noise and starts being news.
//
// It is deliberately COARSE above ten minutes — hours and minutes, never
// seconds — so it stays a fact you can glance at rather than a clock ticking at
// you, and so the repaint budget stays a repaint a minute.
//
// WHY THE LAST STRETCH IS A STRIP AND NOT A MODAL. A modal would be unmissable
// and would also land on top of whoever is typing in the chat — and typing in
// the chat is itself a meaningful action, so that person's room was never going
// to close. The people who see this are the ones doing nothing, and for them a
// full-width alert band under the header, with the header button counting
// seconds beside it, is not something you miss. It is in flow rather than fixed,
// which also keeps it clear of `bottom: 0` and the phone keyboard problem
// entirely. **It is untouched by the popover** and must stay that way: the strip
// is the one piece here allowed to interrupt, and burying it behind a press
// would be burying the only warning that arrives on its own.
//
// ONE CLOCK. No piece reads the deadline for itself. `useRoomExpiry` lives in
// roomExpiryClock.ts, the shell calls it once and hands the same reading to the
// header button and to the strip — because two components calling it was two
// self-rescheduling timeout chains ticking a second apart for the last ten
// minutes of every room, one of them for a component that draws nothing until
// the last two. A third caller would be a third chain.
//
// THE ONE RULE. Nothing in this file, and nothing in RoomInfo.tsx, may send
// `extend-room` except an onClick. No effect, no timer, no dismissal, no
// visibility change, and not opening the popover either. `lastMeaningfulAt`
// exists on the server precisely because a browser tab pings once a second
// whether or not anybody is in the chair, and an automatic extend from here
// would quietly delete the idle sweep and put the abandoned-room problem back.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { ReactElement } from 'react';
import { Button } from '../primitives/Button.js';
import { formatLeft, levelFor } from './roomExpiryClock.js';
import type { RoomExpiry } from './roomExpiryClock.js';

export interface RoomExpiryProps {
  /**
   * The room's single reading of its own deadline, from `useRoomExpiry` in the
   * shell. `msLeft` is null when nothing is counting — which is every phase of
   * a game in progress, because the sweep refuses to touch one. Null is what
   * takes the strip off screen the moment a game starts, with no phase check of
   * its own to keep in step.
   */
  expiry: RoomExpiry;
  /** Sends `extend-room`. Called from an onClick and from nowhere else. */
  onExtend: () => void;
}

/**
 * The last stretch, under the header and across the whole width.
 *
 * `role="status"` rather than `alert`: it is announced once when it arrives and
 * does not interrupt again every second as the number changes, which is what an
 * assertive live region would do with a ticking countdown inside it.
 *
 * Dismissing hides it and **does nothing else**. It is remembered against the
 * deadline it was dismissed for, so if anybody in the room extends, the strip is
 * eligible to come back for the next one — a dismissal is "I have seen this",
 * never "the room is fine". Everything else about coming and going is
 * structural: the strip is fed by `expiresAt`, which the server nulls the moment
 * a game starts, so a phase change out from under it takes it off screen without
 * this component knowing what a phase is.
 */
export function RoomExpiryWarning({ expiry, onExtend }: RoomExpiryProps): ReactElement | null {
  const { msLeft, expiresAt } = expiry;
  const [dismissedFor, setDismissedFor] = useState<number | null>(null);

  if (msLeft === null || levelFor(msLeft) !== 'urgent') return null;
  if (dismissedFor === expiresAt) return null;

  return (
    <div
      role="status"
      // An opaque fill under the tinted border: a wash inside a wash composites
      // deeper than the palette was solved for. `alert` is the border and the
      // number, never a fill behind text.
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-alert/60 bg-surface px-4 py-3 sm:px-8"
    >
      <p className="min-w-0 flex-1 text-[15px] text-ink">
        Nobody has done anything here for a while. The room closes in{' '}
        <span className="tabular font-bold text-alert">{formatLeft(msLeft)}</span>.
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => setDismissedFor(expiresAt)}>
          Dismiss
        </Button>
        <Button size="sm" variant="primary" onClick={onExtend}>
          Keep it open
        </Button>
      </div>
    </div>
  );
}
